import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import LoadingSplash from '@/components/LoadingSplash';
import { ErrorAccessDenied } from '@/components/auth/ErrorAccessDenied';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: UserRole[];
  fallback?: ReactNode;
}

export const ProtectedRoute = ({ 
  children, 
  requiredRoles, 
  fallback 
}: ProtectedRouteProps) => {
  const { user, loading, hasAnyRole, accessDenied, accessError } = useAuth();

  if (loading) {
    return <LoadingSplash title="Loading your dashboard…" subtitle="Authenticating and syncing data" />;
  }

  // Show access denied error screen if user is authenticated but profile failed to load
  if (user && accessDenied) {
    return (
      <ErrorAccessDenied 
        userId={user.id}
        userEmail={user.email || 'Unknown'}
        errorDetails={accessError}
      />
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requiredRoles && !hasAnyRole(requiredRoles)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-medium text-foreground">Access Denied</h2>
          <p className="text-muted-foreground">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};